import type { JSONSchema, JSONSchemaRef } from "../ir.js";

function unwrapSchema(ref: JSONSchemaRef | undefined): JSONSchema | undefined {
  if (!ref) {
    return undefined;
  }
  if ("schema" in ref && ref.schema && typeof ref.schema === "object") {
    return ref.schema as JSONSchema;
  }
  if ("$ref" in ref) {
    return undefined;
  }
  return ref as JSONSchema;
}

function isValidIdentifier(name: string): boolean {
  return /^[a-zA-Z_$][a-zA-Z0-9_$]*$/.test(name);
}

function quoteKey(name: string): string {
  return isValidIdentifier(name) ? name : JSON.stringify(name);
}

function schemaTypeName(
  schema: JSONSchema | undefined,
  fallback: string,
): string {
  if (!schema) {
    return fallback;
  }
  const title = schema["title"];
  if (typeof title === "string" && title.length > 0) {
    return title;
  }
  return fallback;
}

/**
 * Convert a kebab-case or snake_case name to PascalCase.
 */
export function toPascalCase(name: string): string {
  return name
    .split(/[-_]/)
    .filter((part) => part.length > 0)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join("");
}

/**
 * Convert a kebab-case or snake_case name to camelCase.
 */
export function toCamelCase(name: string): string {
  const pascal = toPascalCase(name);
  if (pascal.length === 0) {
    return name;
  }
  return pascal.charAt(0).toLowerCase() + pascal.slice(1);
}

function jsonSchemaToTs(
  schema: JSONSchema | undefined,
  indent: string,
  generated: Set<string>,
): string {
  if (!schema) {
    return "unknown";
  }

  const enumValues = schema["enum"];
  if (Array.isArray(enumValues) && enumValues.length > 0) {
    return enumValues
      .map((v) => (typeof v === "string" ? JSON.stringify(v) : String(v)))
      .join(" | ");
  }

  const type = schema["type"];
  if (type === "string") {
    return "string";
  }
  if (type === "number" || type === "integer") {
    return "number";
  }
  if (type === "boolean") {
    return "boolean";
  }
  if (type === "array") {
    const items = schema["items"];
    if (items && typeof items === "object" && !Array.isArray(items)) {
      return `${jsonSchemaToTs(items as JSONSchema, indent, generated)}[]`;
    }
    return "unknown[]";
  }
  if (type === "object" || schema["properties"]) {
    const properties = schema["properties"];
    if (!properties || typeof properties !== "object") {
      return "Record<string, unknown>";
    }

    const required = Array.isArray(schema["required"])
      ? (schema["required"] as string[])
      : [];
    const lines: string[] = [];

    for (const [propName, propSchema] of Object.entries(
      properties as Record<string, unknown>,
    )) {
      if (!propSchema || typeof propSchema !== "object") {
        continue;
      }
      const optional = required.includes(propName) ? "" : "?";
      const tsType = jsonSchemaToTs(
        propSchema as JSONSchema,
        indent + "  ",
        generated,
      );
      lines.push(`${indent}  ${quoteKey(propName)}${optional}: ${tsType};`);
    }

    if (lines.length === 0) {
      return "Record<string, unknown>";
    }
    return `{\n${lines.join("\n")}\n${indent}}`;
  }

  return "unknown";
}

export function schemaRefToTsType(
  ref: JSONSchemaRef | undefined,
  fallback = "unknown",
): string {
  return jsonSchemaToTs(unwrapSchema(ref), "", new Set()) || fallback;
}

export function operationTypeNames(operationName: string): {
  input: string;
  output: string;
  options: string;
  failure: string;
  failureClass: string;
} {
  const base = toPascalCase(operationName);
  return {
    input: `${base}Input`,
    output: `${base}Output`,
    options: `${base}Options`,
    failure: `${base}Failure`,
    failureClass: `${base}FailureClass`,
  };
}

export function interfaceFromSchema(
  name: string,
  ref: JSONSchemaRef | undefined,
  fallbackBody: string,
): string | undefined {
  const schema = unwrapSchema(ref);
  if (!schema) {
    return undefined;
  }

  const body = jsonSchemaToTs(schema, "", new Set());
  if (body === "unknown" && !schema["properties"]) {
    return `export type ${name} = ${fallbackBody};\n`;
  }
  if (body.startsWith("{")) {
    return `export interface ${name} ${body}\n`;
  }
  return `export type ${name} = ${body};\n`;
}

export { unwrapSchema, schemaTypeName };
